/* ==========================================================================
   Levano CRO additions — added 2026-08-16

   Keeps the free-shipping progress bar in sync with the cart.

   The cart drawer's line items and subtotal are replaced client side, so the
   Liquid-rendered bar in snippets/levano-free-shipping-bar.liquid would go stale
   after the first add-to-cart. Three independent triggers are used, because the
   cart is mutated by the theme, by the bundle-discount app, and by direct Cart
   AJAX calls, and no single signal catches all three:

     1. fetch / XMLHttpRequest calls to /cart/{add,change,update,clear}
     2. the common cart events themes and apps dispatch
     3. a MutationObserver on the subtotal node the theme rewrites

   The threshold is NOT duplicated here — it is read from data-lv-threshold on
   the bar, which Liquid renders. Edit the amounts in the snippet only.
   ========================================================================== */
(function () {
  'use strict';

  var SELECTOR = '[data-lv-free-shipping]';
  var CART_WRITE = /\/cart\/(add|change|update|clear)/i;
  var pending = null;

  function formatMoney(cents, currency) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2
      }).format(cents / 100);
    } catch (e) {
      return (cents / 100).toFixed(2) + ' ' + currency;
    }
  }

  function paint(bar, cart) {
    var threshold = parseInt(bar.getAttribute('data-lv-threshold'), 10);
    if (!threshold || threshold <= 0) return;

    var total = cart.total_price || 0;
    var remaining = threshold - total;
    var done = remaining <= 0;
    var pct = done ? 100 : Math.round((total * 100) / threshold);

    bar.classList.toggle('lv-ship--done', done);

    var msg = done
      ? 'Free shipping unlocked'
      : 'You’re <strong>' + formatMoney(remaining, cart.currency) + '</strong> away from free shipping';

    bar.innerHTML =
      '<p class="lv-ship__msg">' + msg + '</p>' +
      '<div class="lv-ship__track" role="progressbar" aria-valuemin="0" aria-valuemax="100"' +
      ' aria-valuenow="' + pct + '" aria-label="Progress toward free shipping">' +
      '<span class="lv-ship__fill" style="width: ' + pct + '%;"></span>' +
      '</div>';
  }

  function cartUrl() {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root.replace(/\/$/, '') + '/cart.js';
  }

  function refresh() {
    if (!document.querySelector(SELECTOR)) return;

    clearTimeout(pending);
    pending = setTimeout(function () {
      fetch(cartUrl(), { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (cart) {
          if (!cart) return;
          Array.prototype.forEach.call(document.querySelectorAll(SELECTOR), function (bar) {
            paint(bar, cart);
          });
        })
        .catch(function () { /* leave the server-rendered state in place */ });
    }, 150);
  }

  // 1. Network — catches the theme, apps, and direct Cart AJAX calls alike.
  function hookNetwork() {
    if (window.fetch) {
      var nativeFetch = window.fetch;
      window.fetch = function (input) {
        var url = typeof input === 'string' ? input : (input && input.url) || '';
        var promise = nativeFetch.apply(this, arguments);
        if (CART_WRITE.test(url)) {
          promise.then(refresh, function () {});
        }
        return promise;
      };
    }

    if (window.XMLHttpRequest) {
      var nativeOpen = XMLHttpRequest.prototype.open;
      XMLHttpRequest.prototype.open = function (method, url) {
        if (CART_WRITE.test(url || '')) {
          this.addEventListener('load', refresh);
        }
        return nativeOpen.apply(this, arguments);
      };
    }
  }

  // 2. Events — themes and apps commonly dispatch one of these.
  function hookEvents() {
    ['cart:updated', 'cart:refresh', 'cart:change', 'cart:build', 'ajaxProduct:added']
      .forEach(function (name) {
        document.addEventListener(name, refresh);
      });
  }

  // 3. DOM — the theme rewrites [data-subtotal] on every cart change.
  function hookDom() {
    var targets = document.querySelectorAll('[data-subtotal], [data-products]');
    if (!targets.length) return false;

    var observer = new MutationObserver(refresh);
    Array.prototype.forEach.call(targets, function (node) {
      observer.observe(node, { childList: true, characterData: true, subtree: true });
    });
    return true;
  }

  function init() {
    hookNetwork();
    hookEvents();

    if (!hookDom()) {
      var bodyObserver = new MutationObserver(function () {
        if (hookDom()) bodyObserver.disconnect();
      });
      bodyObserver.observe(document.body, { childList: true, subtree: true });
    }

    refresh();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
